import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Building2, CalendarDays, MessageSquare, TrendingUp, UserPlus, Check, X, Loader2, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import dayjs from 'dayjs';
import { adminApi, adminErrorMsg } from '../../admin/adminApiClient';
import { toast } from 'sonner';

function KpiCard({ icon: Icon, label, value, tint, fg, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${tint} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${fg}`} />
      </div>
      <div>
        <p className="text-2xl font-black text-gray-900">{value ?? '—'}</p>
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [pendingOrgs, setPendingOrgs] = useState([]);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [dailyEnq, setDailyEnq] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const from = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
      const to = dayjs().format('YYYY-MM-DD');
      const [sumR, orgR, evR, enqR] = await Promise.all([
        adminApi.get('/reports/summary'),
        adminApi.get('/organizers', { params: { status: 'pending', limit: 5 } }),
        adminApi.get('/events', { params: { status: 'submitted', limit: 5 } }),
        adminApi.get('/reports/enquiries', { params: { from_: from, to } }),
      ]);
      setSummary(sumR.data);
      setPendingOrgs(orgR.data?.items || orgR.data || []);
      setPendingEvents(evR.data?.items || evR.data || []);
      setDailyEnq(enqR.data?.daily || []);
    } catch (e) {
      console.error('Dashboard load error', adminErrorMsg(e));
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const approveOrg = async (id) => {
    try {
      await adminApi.post(`/organizers/${id}/approve`);
      toast.success('Organizer approved');
      setPendingOrgs((prev) => prev.filter((o) => (o.id || o.user_id) !== id));
    } catch (e) { toast.error(adminErrorMsg(e)); }
  };

  const rejectOrgInline = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    try {
      await adminApi.post(`/organizers/${id}/reject`, { reason });
      toast.success('Organizer rejected');
      setPendingOrgs((prev) => prev.filter((o) => (o.id || o.user_id) !== id));
    } catch (e) { toast.error(adminErrorMsg(e)); }
  };

  const approveEvent = async (id) => {
    try {
      await adminApi.post(`/events/${id}/approve`);
      toast.success('Event approved → live');
      setPendingEvents((prev) => prev.filter((e) => e.id !== id));
    } catch (e) { toast.error(adminErrorMsg(e)); }
  };

  const rejectEventInline = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    try {
      await adminApi.post(`/events/${id}/reject`, { reason });
      toast.success('Event rejected');
      setPendingEvents((prev) => prev.filter((e) => e.id !== id));
    } catch (e) { toast.error(adminErrorMsg(e)); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-magenta" /></div>;
  }

  return (
    <div data-testid="admin-dashboard-page">
      <h1 className="text-xl font-black text-gray-900 mb-4">Dashboard</h1>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
        <KpiCard icon={Users} label="Total Users" value={summary?.users} tint="bg-blue-50" fg="text-blue-600" testId="kpi-users" />
        <KpiCard icon={Building2} label="Organizers" value={summary?.organizers} tint="bg-purple-50" fg="text-purple-600" testId="kpi-organizers" />
        <KpiCard icon={CalendarDays} label="Live Events" value={summary?.live_events} tint="bg-emerald-50" fg="text-emerald-600" testId="kpi-live-events" />
        <KpiCard icon={MessageSquare} label="Enquiries" value={summary?.total_enquiries} tint="bg-amber-50" fg="text-amber-600" testId="kpi-enquiries" />
        <KpiCard icon={TrendingUp} label="Enq. This Week" value={summary?.enquiries_this_week} tint="bg-pink-50" fg="text-brand-magenta" testId="kpi-enq-week" />
        <KpiCard icon={UserPlus} label="New Users (Wk)" value={summary?.users_this_week} tint="bg-cyan-50" fg="text-cyan-600" testId="kpi-users-week" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        {/* Pending Organizers */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="pending-orgs-widget">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-bold text-gray-900">Applications awaiting review</h2>
            <button onClick={() => navigate('/admin/organizers')} className="text-brand-magenta text-xs font-bold flex items-center gap-0.5 hover:text-pink-700">
              View all <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          {pendingOrgs.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No pending applications</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {pendingOrgs.map((o) => {
                const oid = o.id || o.user_id;
                return (
                  <div key={oid} className="flex items-center gap-3 px-4 py-2.5" data-testid={`pending-org-${oid}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{o.organization_name || o.full_name}</p>
                      <p className="text-[11px] text-gray-500">{o.contact_email || o.email}</p>
                    </div>
                    <button data-testid={`approve-org-${oid}`} onClick={() => approveOrg(oid)} className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center hover:bg-emerald-100 transition-colors" title="Approve">
                      <Check className="w-4 h-4 text-emerald-600" />
                    </button>
                    <button data-testid={`reject-org-${oid}`} onClick={() => rejectOrgInline(oid)} className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center hover:bg-red-100 transition-colors" title="Reject">
                      <X className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pending Events */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="pending-events-widget">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-bold text-gray-900">Events awaiting review</h2>
            <button onClick={() => navigate('/admin/events')} className="text-brand-magenta text-xs font-bold flex items-center gap-0.5 hover:text-pink-700">
              View all <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          {pendingEvents.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No events pending review</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {pendingEvents.map((ev) => (
                <div key={ev.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`pending-event-${ev.id}`}>
                  <img src={ev.banner_url} alt="" className="w-10 h-10 rounded-lg object-cover bg-gray-100 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">{ev.title}</p>
                    <p className="text-[11px] text-gray-500">{ev.organizer_name} · {ev.city}</p>
                  </div>
                  <button data-testid={`approve-event-${ev.id}`} onClick={() => approveEvent(ev.id)} className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center hover:bg-emerald-100 transition-colors" title="Approve">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </button>
                  <button data-testid={`reject-event-${ev.id}`} onClick={() => rejectEventInline(ev.id)} className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center hover:bg-red-100 transition-colors" title="Reject">
                    <X className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        {/* Top Categories */}
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="chart-categories">
          <h2 className="text-sm font-bold text-gray-900 mb-3">Top Categories</h2>
          {summary?.top_categories?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={summary.top_categories}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#E91E63" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-gray-400 text-center py-10">No data</p>
          )}
        </div>

        {/* Top Cities */}
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="chart-cities">
          <h2 className="text-sm font-bold text-gray-900 mb-3">Top Cities</h2>
          {summary?.top_cities?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={summary.top_cities}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-gray-400 text-center py-10">No data</p>
          )}
        </div>
      </div>

      {/* Daily Enquiries */}
      <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="chart-daily-enquiries">
        <h2 className="text-sm font-bold text-gray-900 mb-3">Daily Enquiries (Last 7 Days)</h2>
        {dailyEnq.length ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={dailyEnq}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => dayjs(d).format('MMM D')} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip labelFormatter={(d) => dayjs(d).format('ddd, MMM D')} />
              <Line type="monotone" dataKey="count" stroke="#E91E63" strokeWidth={2} dot={{ r: 4, fill: '#E91E63' }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-gray-400 text-center py-10">No enquiry data for this period</p>
        )}
      </div>
    </div>
  );
}
